import { isEqual } from 'lodash'
import { EditorState } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { schema } from '../../Schema'
import { FloatingDropdown } from '../../components/FloatingDropdown/FloatingDropdown'
import { Suggestions } from './Suggestions'
import { ANCHOR_DATA_ATTR, EMOJI_PLUGIN_KEY } from './constants'
import { createRoot, Root } from 'react-dom/client'

export class EmojiView {
  initialized: boolean = false
  view: EditorView
  container: HTMLDivElement
  root: Root

  constructor(view: EditorView) {
    this.view = view
    this.container = document.createElement('div')
    Object.assign(this.container.style, { visibility: 'hidden' })
    this.root = createRoot(this.container)
  }

  initialize() {
    this.view.dom.parentNode?.appendChild(this.container)
    this.mountComponent('')
    this.initialized = true
  }

  update = (view: EditorView, prevState: EditorState) => {
    if (!this.initialized) {
      this.initialize()
    }
    this.view = view
    const prev = EMOJI_PLUGIN_KEY.getState(prevState)
    const next = EMOJI_PLUGIN_KEY.getState(view.state)

    if (!prev || !next) {
      return
    }

    const opening = !prev.active && next.active
    const moving = prev.active && next.active && !isEqual(prev.range, next.range)
    const closing = prev.active && !next.active

    if (opening || moving) {
      this.displayPopover(next.text ?? '')
    }

    if (closing) {
      this.displayPopover('')
    }
  }

  destroy = () => {
    this.root.render(<></>)
  }

  private mountComponent = (text: string = '') => {
    const anchor = this.view.dom.querySelector(`[${ANCHOR_DATA_ATTR}]`)
    this.root.render(
      <FloatingDropdown anchor={anchor}>
        <Suggestions view={this.view} query={text} onSelect={this.insertEmoji} />
      </FloatingDropdown>,
    )
  }

  private displayPopover = (text: string) => {
    this.mountComponent(text)
    this.container.style.visibility = 'visible'
  }

  private insertEmoji = (emoji: string) => {
    const state = EMOJI_PLUGIN_KEY.getState(this.view.state)
    if (!state?.range) {
      return
    }
    const tr = this.view.state.tr
    tr.replaceWith(state.range.from, state.range.to, schema.text(emoji))
    this.view.dispatch(tr)
    this.view.focus()
  }
}
